import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { api } from '../lib/api';

interface NavbarProps {
  user: any;
  onNavigate: (page: string, data?: any) => void;
  onLogout: () => void;
  currentPage: string;
}

const TYPE_ICONS: Record<string, string> = {
  new_chapter: '📖',
  new_follower: '👤',
  new_review: '⭐',
  new_tip: '💰',
  new_comment: '💬',
  admin_unpublish: '⚠️',
  admin_republish: '✅',
  level_up: '🎉',
  badge_earned: '🏅',
};

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function Navbar({ user, onNavigate, onLogout, currentPage }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = useCallback(() => {
    if (!user) return;
    api.users.unreadCount().then(res => setUnreadCount(res.count)).catch(() => {});
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!notifOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  const openNotifications = async () => {
    if (notifOpen) {
      setNotifOpen(false);
      return;
    }
    setNotifOpen(true);
    setNotifLoading(true);
    try {
      const [notifs] = await Promise.all([
        api.users.notifications(),
        api.users.markNotificationsRead(),
      ]);
      setNotifications(notifs.slice(0, 15));
      setUnreadCount(0);
    } catch {}
    setNotifLoading(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.users.markNotificationsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleNotifClick = (notif: any) => {
    if (notif.link) {
      const path = notif.link.replace(/^https?:\/\/[^/]+/, '').replace(/^\//, '');
      onNavigate(path);
    }
    setNotifOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <button onClick={() => onNavigate('home')} className="flex items-center gap-2 group">
          <img src="/logo.png" alt="ScribeHaven" className="w-8 h-8 rounded-full" />
          <span className="text-xl font-bold bg-gradient-to-r from-ink-400 to-ink-600 bg-clip-text text-transparent">
            ScribeHaven
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {[
            { key: 'home', label: 'Home' },
            { key: 'discover', label: 'Discover' },
            { key: 'leaderboard', label: 'Leaderboard' },
            { key: 'contest', label: '🏆 Contest' },
            { key: 'forums', label: 'Forums' },
          ].map(item => (
            <button
              key={item.key}
              onClick={() => onNavigate(item.key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                currentPage === item.key
                  ? 'bg-ink-600/20 text-ink-400'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
            >
              {item.label}
            </button>
          ))}

          <div className="w-px h-6 bg-gray-700 mx-1" />

          <button
            onClick={() => onNavigate('discover')}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentPage === 'discover'
                ? 'bg-ink-600/20 text-ink-400'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Read
          </button>

          <button
            onClick={() => onNavigate('dashboard')}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentPage === 'dashboard'
                ? 'bg-ink-600/20 text-ink-400'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Write
          </button>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-gray-400 hover:text-yellow-400 hover:bg-gray-800 transition-colors"
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          {user ? (
            <div className="relative flex items-center gap-2">
              <button
                onClick={() => { onNavigate('ink'); setMenuOpen(false); }}
                className="text-amber-400 text-sm font-medium hidden sm:flex items-center gap-1 hover:text-amber-300 transition-colors"
                title="Your Ink balance — click to learn more"
              >
                <span>✦</span> {user.coinsBalance?.toLocaleString() ?? 0}
              </button>

              <button
                onClick={() => { onNavigate('messages'); setMenuOpen(false); }}
                className={`relative p-2 rounded-lg transition-colors ${
                  currentPage === 'messages' ? 'text-ink-400 bg-ink-600/20' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                }`}
                aria-label="Messages"
                title="Messages"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </button>

              <div className="relative" ref={notifRef}>
                <button
                  onClick={openNotifications}
                  className="relative p-2 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div className="absolute right-0 top-12 w-80 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                      <span className="text-sm font-semibold text-gray-200">Notifications</span>
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-ink-400 hover:text-ink-300 transition-colors"
                      >
                        Mark all as read
                      </button>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="w-5 h-5 border-2 border-ink-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 text-sm">No notifications yet</div>
                      ) : (
                        notifications.map(notif => (
                          <button
                            key={notif.id}
                            onClick={() => handleNotifClick(notif)}
                            className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-800 transition-colors ${
                              !notif.read ? 'bg-gray-800/50' : ''
                            } ${notif.link ? 'cursor-pointer' : 'cursor-default'}`}
                          >
                            <span className="text-lg flex-shrink-0 mt-0.5">{TYPE_ICONS[notif.type] || '🔔'}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-300 leading-snug">{notif.message}</p>
                              <p className="text-xs text-gray-500 mt-1">{relativeTime(notif.createdAt)}</p>
                            </div>
                            {!notif.read && (
                              <span className="w-2 h-2 rounded-full bg-ink-400 flex-shrink-0 mt-2" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              >
                <div className="relative">
                  <div className="w-7 h-7 rounded-full bg-ink-600 flex items-center justify-center text-xs font-bold">
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt={user.username} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      user.username[0].toUpperCase()
                    )}
                  </div>
                  {user.level && (
                    <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-ink-700 border border-gray-800 text-[9px] font-bold flex items-center justify-center">
                      {user.level}
                    </span>
                  )}
                </div>
                <span className="hidden sm:block text-sm font-medium">{user.username}</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-12 w-56 bg-gray-800 border border-gray-700 rounded-xl shadow-xl py-1 z-50">
                  {/* XP / Level mini-bar */}
                  {user.level !== undefined && user.xp !== undefined && (
                    <div className="px-4 py-3 border-b border-gray-700">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-ink-400">Level {user.level}</span>
                        <span className="text-xs text-gray-500">{user.xp?.toLocaleString()} XP</span>
                      </div>
                      {(() => {
                        const xpForLevel = (lvl: number) => lvl <= 1 ? 0 : Math.floor(50 * lvl * (lvl - 1));
                        const cur = xpForLevel(user.level);
                        const next = xpForLevel(user.level + 1);
                        const pct = next > cur ? Math.min(100, Math.round(((user.xp - cur) / (next - cur)) * 100)) : 100;
                        return (
                          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-ink-500 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        );
                      })()}
                      <p className="text-[10px] text-gray-600 mt-1">
                        {(() => {
                          const xpForLevel = (lvl: number) => lvl <= 1 ? 0 : Math.floor(50 * lvl * (lvl - 1));
                          const next = xpForLevel(user.level + 1);
                          return `${Math.max(0, next - user.xp).toLocaleString()} XP to Level ${user.level + 1}`;
                        })()}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => { onNavigate('profile', { username: user.username }); setMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-700 text-gray-300"
                  >
                    My Profile
                  </button>
                  <button
                    onClick={() => { onNavigate('library'); setMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-700 text-gray-300"
                  >
                    My Library
                  </button>
                  <button
                    onClick={() => { onNavigate('dashboard'); setMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-700 text-gray-300"
                  >
                    Author Dashboard
                  </button>
                  <button
                    onClick={() => { onNavigate('pen-names'); setMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-700 text-gray-300"
                  >
                    Pen Names
                  </button>
                  {user.role === 'ADMIN' && (
                    <button
                      onClick={() => { onNavigate('admin'); setMenuOpen(false); }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-700 text-red-400"
                    >
                      Content Moderation
                    </button>
                  )}
                  <button
                    onClick={() => { onNavigate('account-settings'); setMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-700 text-gray-300"
                  >
                    Account Settings
                  </button>
                  <hr className="border-gray-700 my-1" />
                  <button
                    onClick={() => { onLogout(); setMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-700 text-red-400"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => onNavigate('login')}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => onNavigate('register')}
                className="px-4 py-2 text-sm font-medium bg-ink-600 hover:bg-ink-700 rounded-lg transition-colors"
              >
                Join Free
              </button>
            </div>
          )}

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}
